import { motion } from "framer-motion";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { formatUSD } from "@/lib/apiClient";
import { Wallet, LogOut, Sun, Moon } from "lucide-react";
import { toast } from "sonner";
import AuthModal from "@/components/AuthModal";
import WalletModal from "@/components/WalletModal";

const links = [
  { label: "Collection", href: "/#collection", type: "anchor" },
  { label: "About", to: "/about", type: "route" },
  { label: "FAQ", to: "/faq", type: "route" },
  { label: "Contact", to: "/contact", type: "route" },
];

function ThemeToggle() {
  const { theme, toggleTheme } = useTheme();
  return (
    <button
      onClick={toggleTheme}
      data-testid="theme-toggle"
      aria-label={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
      title={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
      className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full border border-gold/30 text-gold hover:bg-gold/10 hover:border-gold/60 transition-colors shrink-0"
    >
      {theme === "light" ? <Moon size={16} /> : <Sun size={16} />}
    </button>
  );
}

export default function Nav({ onOpenAuth, onOpenWallet }) {
  const { user, logout } = useAuth();
  const selfManaged = !onOpenAuth && !onOpenWallet;
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [walletOpen, setWalletOpen] = useState(false);

  const handleAuth = (mode) => {
    if (onOpenAuth) return onOpenAuth(mode);
    setAuthMode(mode || "login");
    setAuthOpen(true);
  };
  const handleWallet = () => {
    if (onOpenWallet) return onOpenWallet();
    setWalletOpen(true);
  };

  const doLogout = async () => {
    await logout();
    toast.success("Signed out.");
  };

  return (
    <motion.nav
      data-testid="main-nav"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-50 bg-[var(--nav-bg)] backdrop-blur-md"
    >
      <div className="max-w-[1600px] mx-auto px-2.5 sm:px-6 md:px-12 h-16 sm:h-20 flex items-center justify-between gap-2">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 sm:gap-3.5 group shrink-0 min-w-0">
          <span className="w-2.5 h-2.5 rotate-45 bg-gold shrink-0 group-hover:scale-125 transition-transform duration-500" />
          <span className="font-mono max-[360px]:text-xs text-sm sm:text-xl md:text-2xl font-medium tracking-tight text-ink whitespace-nowrap">elite-domains<span className="text-gold">.com</span></span>
        </Link>

        <div className="hidden md:flex items-center gap-10">
          {links.map((l) =>
            l.type === "route" ? (
              <Link
                key={l.label}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="font-mono text-xs uppercase tracking-[0.2em] text-ink-dim hover:text-ink transition-colors duration-300 hover-underline"
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.label}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="font-mono text-xs uppercase tracking-[0.2em] text-ink-dim hover:text-ink transition-colors duration-300 hover-underline"
              >
                {l.label}
              </a>
            )
          )}
        </div>

        {user ? (
          <div className="flex items-center gap-2 sm:gap-3">
            <ThemeToggle />
            <button
              onClick={handleWallet}
              data-testid="nav-wallet"
              className="flex items-center gap-2 rounded-full border border-gold/40 text-gold font-mono text-[11px] sm:text-xs px-3 sm:px-4 py-2 sm:py-2.5 hover:bg-gold/10 transition-colors"
            >
              <Wallet size={14} />
              <span data-testid="nav-wallet-balance">{formatUSD(user.wallet_balance)}</span>
            </button>
            <Link to="/account" data-testid="nav-avatar" title="My account" className="hidden sm:flex w-9 h-9 rounded-full bg-gold/15 border border-gold/30 items-center justify-center font-mono text-sm text-gold hover:bg-gold/25 transition-colors">
              {(user.name || user.email)[0].toUpperCase()}
            </Link>
            <button onClick={doLogout} data-testid="nav-logout" className="text-ink-dim hover:text-ink transition-colors" title="Sign out">
              <LogOut size={17} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <ThemeToggle />
            <button
              onClick={() => handleAuth("login")}
              data-testid="nav-login"
              className="font-sans font-semibold text-xs sm:text-sm px-2.5 sm:px-5 h-9 sm:h-10 inline-flex items-center rounded-full border border-white/15 text-ink hover:border-gold/60 transition-colors duration-300"
            >
              Login
            </button>
            <button
              onClick={() => handleAuth("register")}
              data-testid="nav-register"
              className="font-sans font-bold text-xs sm:text-sm px-2.5 sm:px-5 h-9 sm:h-10 inline-flex items-center rounded-full bg-gold text-bg hover:shadow-[0_0_22px_rgba(212,175,55,0.4)] transition-shadow duration-400"
            >
              Register
            </button>
          </div>
        )}
      </div>
      {selfManaged && (
        <>
          <AuthModal open={authOpen} onOpenChange={setAuthOpen} initialMode={authMode} />
          <WalletModal open={walletOpen} onOpenChange={setWalletOpen} />
        </>
      )}
    </motion.nav>
  );
}
